import { Component, Input, AfterViewInit, ViewChildren } from '@angular/core';
import { FormGroup, FormBuilder, AbstractControl, ValidatorFn } from '@angular/forms';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { CategoryBase, EditTranslationDto, TranslationDetailsDto, TemplateDto } from '../../../shared/model';
import { SharedService } from '../../../shared/shared.service';

import 'rxjs/add/operator/map';
import { Observable } from 'rxjs';
import { KeywordsViewService } from '../keywords-view.service';

import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-edit-keyword',
  templateUrl: './edit-keyword.component.html',
  styleUrls: ['../../../shared/styles/edit-modal.css']
})
export class EditKeywordComponent implements AfterViewInit {

  @Input() categories: CategoryBase[];
  @Input() keyword: EditTranslationDto = <EditTranslationDto>{};
  @Input() allTranslations: TranslationDetailsDto[];
  @Input() addingKeyword = false;

  @ViewChildren('combobox') comboboxes: any;

  myForm: FormGroup;
  flags: any;
  comadaSuggestions: Array<TemplateDto> = [];
  suggestionsRefreshTimeout: any;
  categoryMissing = false;
  englishMissing = false;

  constructor(
    private keywordsViewService: KeywordsViewService,
    public activeModal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private sharedService: SharedService) {
      this.createForm();
      this.flags = this.sharedService.getFlags();
  }

  ngAfterViewInit() {
    this.setMaxInputLimit();
  }

  createForm() {
    this.myForm = this.formBuilder.group({
      English: ['', [], this.createValidator('English')],
      Category: <CategoryBase> {},
      French: ['', [], this.createValidator('French')],
      German: ['', [], this.createValidator('German')],
      Spanish: ['', [], this.createValidator('Spanish')],
      Dutch: ['', [], this.createValidator('Dutch')],
      Italian: ['', [], this.createValidator('Italian')],
      KeywordId: ''
    });
  }

  createValidator(language: string): ValidatorFn {
    return (control: AbstractControl) => {
      return this.checkIfKeywordExists(control.value, language)
        .map(res => {
          if (control.dirty) {
            const hasChanged = this.checkIfValueChanged(control.value, language);
            return res && hasChanged ? { keywordExists: true } : null;
          }
          return res && control.dirty ? { keywordExists: true } : null;
        });
    };
  }

  checkIfValueChanged(newValue, language): boolean {
    if (this.keyword && this.keyword.TranslationDetails) {
      return (this.addingKeyword || this.keyword.TranslationDetails[language].trim().toLowerCase() !== newValue.trim().toLowerCase());
    }
    return true;
  }

  submitForm() {
    if (this.keyword && this.keyword.TranslationDetails && this.keyword.TranslationDetails.KeywordId && !this.addingKeyword) {
      this.myForm.get('KeywordId').setValue(this.keyword.TranslationDetails.KeywordId);
    }
    if (this.myForm.get('Category').value !== 'undefined') {
      this.activeModal.close(this.myForm.value);
    }
  }

  verifyChanges() {
    this.checkCategory();
    this.checkEnglish();

    if (!this.categoryMissing && !this.englishMissing && this.myForm.valid) {
      this.submitForm();
    }
  }

  public setFormValues(translation) {
    this.myForm.get('English').setValue(translation.TranslationDetails.English);
    this.myForm.get('French').setValue(translation.TranslationDetails.French);
    this.myForm.get('German').setValue(translation.TranslationDetails.German);
    this.myForm.get('Spanish').setValue(translation.TranslationDetails.Spanish);
    this.myForm.get('Dutch').setValue(translation.TranslationDetails.Dutch);
    this.myForm.get('Italian').setValue(translation.TranslationDetails.Italian);
    this.myForm.get('Category').setValue(this.categories.find(c => c.Id === translation.CategoryId));
    this.refreshComadaSuggestions();
  }

  public getTranslation(language): string {
    if (this.keyword && this.keyword.TranslationDetails) {
      return this.keyword.TranslationDetails[language];
    }
    return '';
  }

  getSrcForLang(languageName: string): string {
    return this.flags.find(e => e.name === languageName).src;
  }

  keywordDuplicateExists(lang: string): boolean {
    const control = this.myForm.get(lang);
    if (control) {
      const valid = control.dirty && control.errors && control.errors.keywordExists;
      if (this.keyword) {
        return valid && this.checkIfValueChanged(control.value, lang);
      }
      return valid;
    }
    return false;
  }

  checkIfKeywordExists(value: string, language: string): Observable<any> {
    let duplicateFound = false;

    if (this.allTranslations && !!value) {
      for (let i = 0; i < this.allTranslations.length; i++) {
        if (this.allTranslations[i][language].trim().toLowerCase() === value.trim().toLowerCase()) {
          duplicateFound = true;
          break;
        }
      }
    }

    return new Observable((observer) => {
      observer.next(duplicateFound);
      observer.complete();
    });
  }

  unWrapTranslations(template: TemplateDto): TranslationDetailsDto {
    const transformed: TranslationDetailsDto = {
      English: template.Translations['EN'],
      Dutch: template.Translations['NL'],
      French: template.Translations['FR'],
      German: template.Translations['DE'],
      Italian: template.Translations['IT'],
      Spanish: template.Translations['ES'],
      KeywordId: ''
    };

    return transformed;
  }

  unWrapTranslationsArray(templates: TemplateDto[]): TranslationDetailsDto[] {
    return templates.map(x => this.unWrapTranslations(x));
  }

  wrapTranslations(): TemplateDto {
    const transformed: TemplateDto = {
      TemplateString: '',
      Translations: {}
    };
    transformed.Translations['EN'] = this.myForm.get('English').value;
    transformed.Translations['FR'] = this.myForm.get('French').value;
    transformed.Translations['DE'] = this.myForm.get('German').value;
    transformed.Translations['ES'] = this.myForm.get('Spanish').value;
    transformed.Translations['NL'] = this.myForm.get('Dutch').value;
    transformed.Translations['IT'] = this.myForm.get('Italian').value;
    return transformed;
  }

  getSuggestionsForLanguage(languageCode: string): string[] {
    const code = (languageCode || '').toUpperCase();
    return this.comadaSuggestions
      .map(x => x.Translations[code])
      .filter(x => !!x);
  }

  formEditSuggestionRefresh(value, languageName: string) {
    const self = this;
    this.myForm.controls[languageName].setValue(value);
    if (self.suggestionsRefreshTimeout) {
      clearTimeout(self.suggestionsRefreshTimeout);
    }
    self.suggestionsRefreshTimeout = setTimeout(() => this.refreshComadaSuggestions(), 500);
    this.checkEnglish();
  }

  categoryChange(selectedCategory) {
    this.categoryMissing = selectedCategory === undefined;
  }

  markAllAsDirty() {
    Object.keys(this.myForm.controls).forEach(field => {
      const control = this.myForm.get(field);
      control.markAsDirty();
    });
  }

  private refreshComadaSuggestions() {
    const templateDto = this.wrapTranslations();
    this.keywordsViewService.getComadaSuggestions(templateDto).subscribe(res => {
      this.comadaSuggestions = res;
    },
      () => {
      });
  }

  private checkCategory() {
    this.categoryMissing = !(this.myForm.get('Category').value !== undefined
      && this.myForm.get('Category').value !== null
      && this.myForm.get('Category').value.Id !== undefined);
  }

  private checkEnglish() {
    this.englishMissing = !(this.myForm.get('English').value !== undefined
      && this.myForm.get('English').value !== null
      && this.myForm.get('English').value.trim() !== '');
  }

  private setMaxInputLimit() {
    this.comboboxes._results.forEach(e => {
      const input = this.getInput(e);

      if (input !== null) {
        input.maxLength = environment.maxKeywordLength;
      }
    });
  }

  private getInput(elem: any): any {
    if (!!elem && !!elem.input && !!elem.input.nativeElement) {
      return elem.input.nativeElement;
    }

    if (!!elem.wrapper.children && !!elem.wrapper.children[0]) {
      const span = elem.wrapper.children[0];

      if (!!span.children && !!span.children[0]) {
        const searchbar = span.children[0];

        if (!!searchbar.children && !!searchbar.children[0]) {
          return searchbar.children[0];
        }
      }
    }

    return null;
  }
}
